import {
  type BitteToolResult,
  CustomToolComponent,
  type MessageAppend,
  MessageGroupComponentProps,
  SmartActionAiMessage,
} from '@bitte-ai/types';
import equal from 'fast-deep-equal';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useState } from 'react';
import { cn } from '../../lib/utils';
import { MessageGroup } from './MessageGroup';

interface MessageItemProps {
  meta: {
    chatId: string | undefined;
    accountId: string;
    creator?: string;
    agentName?: string;
    agentImage?: string;
  };
  message: SmartActionAiMessage;
  isLoading: boolean;
  theme?: {
    messageBackgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    showBorder?: boolean;
    requiresScrollPadding?: boolean;
    isWidgetMode?: boolean;
    hasAutoPrompt?: boolean;
  };
  actions: {
    addToolResult: (params: {
      toolCallId: string;
      result: BitteToolResult<unknown>;
    }) => void;
    append: MessageAppend;
    hideToolCall?: boolean;
  };
  customComponents?: {
    messageContainer?: React.ComponentType<MessageGroupComponentProps>;
    toolComponents?: CustomToolComponent[];
  };
}

const MessageItemComponent = ({
  meta,
  message,
  isLoading,
  theme = {},
  actions,
  customComponents = {},
}: MessageItemProps) => {
  // Skip system messages
  if (message.role === 'system') return null;

  // Synchronous height check to prevent delayed re-renders
  const getInitialScreenSize = () => {
    if (typeof window !== 'undefined') {
      return window.innerHeight > 800;
    }
    return true; // Default for SSR
  };

  const [isLargeScreen] = useState(getInitialScreenSize);

  const {
    messageBackgroundColor,
    borderColor,
    textColor,
    showBorder,
    requiresScrollPadding,
    isWidgetMode,
    hasAutoPrompt,
  } = theme;

  const { addToolResult, append, hideToolCall } = actions;
  const { messageContainer, toolComponents } = customComponents;

  // Disable animations for auto-prompt to prevent flickering
  const shouldAnimate = !hasAutoPrompt;

  const messageGroupProps = {
    chatId: meta.chatId,
    groupKey: `message-${message.id}`,
    messages: [message],
    accountId: meta.accountId,
    creator: meta.creator,
    isLoading,
    messageBackgroundColor: messageBackgroundColor || '',
    borderColor: borderColor || '',
    textColor: textColor || '',
    agentImage: meta.agentImage,
    agentName: meta.agentName,
    addToolResult,
    append,
    customMessageContainer: messageContainer,
    customTxContainer: undefined,
    customApproveTxButton: undefined,
    customDeclineTxButton: undefined,
    customToolComponents: toolComponents,
    showBorder: showBorder || false,
    hideToolCall: hideToolCall || false,
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className="bitte-w-full bitte-mx-auto bitte-max-w-3xl bitte-px-4"
        initial={shouldAnimate ? { y: 5, opacity: 0 } : false}
        animate={shouldAnimate ? { y: 0, opacity: 1 } : false}
        data-role={message.role}
      >
        <div
          className={cn('bitte-flex bitte-flex-col bitte-gap-4 bitte-w-full', {
            // Desktop: Full padding (min-h-96 = 384px)
            'bitte-min-h-96':
              message.role === 'assistant' &&
              requiresScrollPadding &&
              !isWidgetMode,

            // Widget on larger screens: Reduced padding (min-h-64 = 256px)
            'bitte-min-h-64':
              message.role === 'assistant' &&
              requiresScrollPadding &&
              isWidgetMode &&
              isLargeScreen,

            // Widget on smaller screens: Minimal padding (min-h-48 = 192px)
            'bitte-min-h-48':
              message.role === 'assistant' &&
              requiresScrollPadding &&
              isWidgetMode &&
              !isLargeScreen,
          })}
        >
          <MessageGroup {...messageGroupProps} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export const MessageItem = memo(
  MessageItemComponent,
  (prevProps, nextProps) => {
    // Early exit optimizations - cheap comparisons first
    if (prevProps.message.id !== nextProps.message.id) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;

    // Check theme changes
    if (
      prevProps.theme?.requiresScrollPadding !==
      nextProps.theme?.requiresScrollPadding
    )
      return false;
    if (prevProps.theme?.showBorder !== nextProps.theme?.showBorder)
      return false;
    if (prevProps.theme?.isWidgetMode !== nextProps.theme?.isWidgetMode)
      return false;

    // Check action changes
    if (prevProps.actions.hideToolCall !== nextProps.actions.hideToolCall)
      return false;

    // Expensive deep comparison last
    if (!equal(prevProps.message, nextProps.message)) return false;

    return true;
  },
);
